<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close', 'createAssistant']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const assistants = useMapGetter('captainAssistants/getRecords');

const currentAssistantId = computed(() => route.params.assistantId);

const isAssistantActive = assistant => {
  return assistant.id === Number(currentAssistantId.value);
};

const fetchDataForRoute = async (routeName, assistantId) => {
  const dataFetchMap = {
    captain_assistants_documents_index: async () => {
      await store.dispatch('captainDocuments/get', { assistantId });
    },
    captain_assistants_scenarios_index: async () => {
      await store.dispatch('captainScenarios/get', { assistantId });
    },
    captain_assistants_playground_index: () => {
      // Playground doesn't need pre-fetching, it loads on interaction
    },
    captain_assistants_inboxes_index: async () => {
      await store.dispatch('captainInboxes/get', { assistantId });
    },
    captain_tools_index: async () => {
      await store.dispatch('captainCustomTools/get', { page: 1 });
    },
    captain_assistants_settings_index: async () => {
      await store.dispatch('captainAssistants/show', assistantId);
    },
  };

  const fetchFn = dataFetchMap[routeName];
  if (fetchFn) {
    await fetchFn();
  }
};

const handleAssistantChange = async assistant => {
  if (isAssistantActive(assistant)) return;

  const currentRouteName = route.name;
  const targetRouteName =
    currentRouteName || 'captain_assistants_overview_index';

  await fetchDataForRoute(targetRouteName, assistant.id);

  await router.push({
    name: targetRouteName,
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });

  emit('close');
};

const openCreateAssistantDialog = () => {
  emit('createAssistant');
  emit('close');
};
</script>

<template>
  <div
    class="absolute z-50 flex max-h-96 w-[min(27.5rem,calc(100vw-2rem))] flex-col gap-4 rounded-xl border border-auth-border bg-auth-surface pt-5 text-auth-text shadow-xl shadow-black/10 dark:border-auth-border-dark dark:bg-auth-surface-dark dark:text-auth-text-dark dark:shadow-black/30"
  >
    <div
      class="flex items-center justify-between gap-4 border-b border-auth-border px-6 pb-4 dark:border-auth-border-dark"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2
            class="w-fit cursor-pointer text-heading-3 text-auth-text hover:underline dark:text-auth-text-dark"
          >
            {{ t('CAPTAIN.ASSISTANT_SWITCHER.ASSISTANTS') }}
          </h2>
        </div>
        <p class="text-body-small text-auth-muted dark:text-auth-muted-dark">
          {{ t('CAPTAIN.ASSISTANT_SWITCHER.SWITCH_ASSISTANT') }}
        </p>
      </div>
      <Button
        :label="t('CAPTAIN.ASSISTANT_SWITCHER.NEW_ASSISTANT')"
        icon="i-lucide-plus"
        size="sm"
        class="!bg-auth-action !text-white hover:!bg-auth-action-hover focus-visible:!outline-auth-action dark:!bg-auth-accent dark:!text-auth-brand dark:hover:!bg-auth-accent-hover dark:focus-visible:!outline-auth-accent"
        @click="openCreateAssistantDialog"
      />
    </div>
    <div
      v-if="assistants.length > 0"
      class="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 overscroll-contain"
    >
      <Button
        v-for="assistant in assistants"
        :key="assistant.id"
        :label="assistant.name"
        variant="ghost"
        color="slate"
        trailing-icon
        :icon="isAssistantActive(assistant) ? 'i-lucide-check' : ''"
        class="h-10 !justify-end !rounded-lg !px-3 !py-2 hover:!bg-auth-soft focus-visible:!outline-auth-action dark:hover:!bg-auth-soft-dark dark:focus-visible:!outline-auth-accent [&>.i-lucide-check]:text-auth-action dark:[&>.i-lucide-check]:text-auth-accent"
        size="sm"
        @click="handleAssistantChange(assistant)"
      >
        <span
          class="truncate text-label-default text-auth-text dark:text-auth-text-dark"
        >
          {{ assistant.name || '' }}
        </span>
        <Avatar
          v-if="assistant"
          :name="assistant.name"
          :size="20"
          icon-name="i-lucide-bot"
          rounded-full
        />
      </Button>
    </div>
    <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
      <p class="text-body-small text-auth-muted dark:text-auth-muted-dark">
        {{ t('CAPTAIN.ASSISTANT_SWITCHER.EMPTY_LIST') }}
      </p>
    </div>
  </div>
</template>
